import { Capacitor } from '@capacitor/core'
import * as Sentry from '@sentry/browser'
import {
  updatePermissionState,
  hasAskedBefore,
  shouldShowDeniedDialog,
  markDeniedDialogShown
} from './utils'
import {
  createStrategy,
  getRegisteredTypes
} from './strategies'
import type {
  PermissionState,
  PermissionType,
  PermissionResult,
  PermissionMetadata,
  PlatformContext,
  UsePermissionsOptions
} from './types'

export type {
  PermissionState,
  PermissionType,
  PermissionResult,
  PermissionMetadata,
  PlatformContext,
  UsePermissionsOptions
}

export function usePermissions (options: UsePermissionsOptions = {}) {
  const dialog = useDialog()
  const snackbar = useSnackbar()

  const states = ref<Record<PermissionType, PermissionState>>(
      {} as Record<PermissionType, PermissionState>
  )
  const isChecking = ref(false)
  const isRequesting = ref(false)

  let platformContextCache: PlatformContext | null = null

  const getContext = (): PlatformContext => {
    if (!platformContextCache) {
      const isNative = Capacitor.isNativePlatform()
      let platform: 'ios' | 'android' | 'web' = 'web'

      if (isNative) {
        const platformInfo = Capacitor.getPlatform()
        platform = platformInfo === 'ios' ? 'ios' : 'android'
      }

      platformContextCache = { isMobile: isNative, platform }
    }
    return platformContextCache
  }

  const check = async (type: PermissionType): Promise<PermissionResult> => {
    try {
      isChecking.value = true
      const context = getContext()
      const strategy = createStrategy(type, context)

      if (!strategy) {
        return { state: 'unsupported', isGranted: false, canRequest: false }
      }

      if (!strategy.isSupported()) {
        states.value[type] = 'unsupported'
        return { state: 'unsupported', isGranted: false, canRequest: false }
      }

      const result = await strategy.check()
      states.value[type] = result.state

      updatePermissionState(type, { lastKnownState: result.state })

      return result
    } catch (error) {
      Sentry.captureException(error)
      return { state: 'denied', isGranted: false, canRequest: false }
    } finally {
      isChecking.value = false
    }
  }

  const request = async (
    type: PermissionType,
    showExplanation: boolean = options.showExplanationDialog ?? true
  ): Promise<PermissionResult> => {
    try {
      isRequesting.value = true
      const context = getContext()
      const strategy = createStrategy(type, context)

      if (!strategy) {
        return { state: 'unsupported', isGranted: false, canRequest: false }
      }

      if (!strategy.isSupported()) {
        snackbar.warning(
            `${strategy.metadata.title} is not supported on this platform.`
        )
        return { state: 'unsupported', isGranted: false, canRequest: false }
      }

      const currentState = await strategy.check()

      if (currentState.isGranted) {
        states.value[type] = 'granted'
        options.onGranted?.(type)
        return currentState
      }

      if (currentState.state === 'denied') {
        if (shouldShowDeniedDialog(type)) {
          await showDeniedDialogAsync(strategy.metadata)
          markDeniedDialogShown(type)
        }
        options.onDenied?.(type)
        return currentState
      }

      if (showExplanation && !hasAskedBefore(type)) {
        const shouldProceed = await showExplanationDialogAsync(
          strategy.metadata
        )
        if (!shouldProceed) {
          return { state: 'prompt', isGranted: false, canRequest: true }
        }
      }

      const result = await strategy.request()
      states.value[type] = result.state

      updatePermissionState(type, {
        lastKnownState: result.state,
        askedAt: Date.now()
      })

      if (result.isGranted) {
        snackbar.success(`${strategy.metadata.title} granted`)
        options.onGranted?.(type)
      } else {
        snackbar.warning(`${strategy.metadata.title} was not granted`)
        options.onDenied?.(type)
      }

      return result
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error(`Failed to request ${type} permission`)
      return { state: 'denied', isGranted: false, canRequest: false }
    } finally {
      isRequesting.value = false
    }
  }

  const state = (type: PermissionType): PermissionState => {
    return states.value[type] || 'prompt'
  }

  const isGranted = (type: PermissionType): boolean => {
    return states.value[type] === 'granted'
  }

  const checkMultiple = async (
    types: PermissionType[]
  ): Promise<Record<PermissionType, PermissionResult>> => {
    const results = {} as Record<PermissionType, PermissionResult>
    await Promise.all(
      types.map(async (type) => {
        results[type] = await check(type)
      })
    )
    return results
  }

  const requestMultiple = async (
    types: PermissionType[]
  ): Promise<Record<PermissionType, PermissionResult>> => {
    const results = {} as Record<PermissionType, PermissionResult>
    for (const type of types) {
      results[type] = await request(type)
    }
    return results
  }

  const showExplanationDialogAsync = (
    metadata: PermissionMetadata
  ): Promise<boolean> => {
    return new Promise((resolve) => {
      dialog.info({
        title: metadata.title,
        text: metadata.description,
        action: () => resolve(true),
        cancelAction: () => resolve(false),
        dialogProps: {
          confirmText: 'Understood',
          showCancel: true,
          confirmButtonProps: { block: true }
        }
      })
    })
  }

  const showDeniedDialogAsync = (
    metadata: PermissionMetadata
  ): Promise<void> => {
    return new Promise((resolve) => {
      dialog.error({
        title: metadata.deniedTitle,
        text: metadata.deniedDescription,
        action: () => resolve(),
        dialogProps: {
          confirmText: 'Understood',
          showCancel: false,
          confirmButtonProps: { block: true }
        }
      })
    })
  }

  const isMicrophoneGranted = computed(() => isGranted('microphone'))
  const areNotificationsGranted = computed(() => isGranted('notifications'))
  const areContactsGranted = computed(() => isGranted('contacts'))

  return {
    states,
    isChecking,
    isRequesting,

    check,
    request,
    state,
    isGranted,
    checkMultiple,
    requestMultiple,

    isMicrophoneGranted,
    areNotificationsGranted,
    areContactsGranted,

    availableTypes: getRegisteredTypes()
  }
}
